import {
  Droplet,
  Factory,
  Flame,
  Globe,
  Leaf,
  LucideIcon,
  Mountain,
  Radiation,
  Recycle,
  Skull,
  Sprout,
  Sun,
  Triangle,
  Wind
} from "lucide-react";
import React, { useState } from "react";
import { formatNumberToScientific } from "smart-decimal-format";
import { Card, CardContent } from "../ui/common/card";
import { Tabs, TabsList, TabsTrigger } from "../ui/common/tabs";

export interface ImpactCategoryItem {
  key?: string;
  indicator: string;
  measure: number;
  unit: string;
}

interface ImpactCardsGridProps {
  impactData: ImpactCategoryItem[];
  /**
   * Optional context shown before the impact-category count, e.g. "Version 5 · EF 3.1" -- so a
   * caller that has several calculations to disambiguate (VersionHistoryPanel) can identify which
   * one this grid belongs to. Other callers (a single product/ingredient's own summary) have
   * nothing to disambiguate and omit it.
   */
  contextLabel?: string;
}

// Icon mapping for each impact category
const categoryIcons: Record<string, LucideIcon> = {
  "Climate change": Globe,
  "Ozone depletion": Sun,
  "Ionising radiation": Radiation,
  "Photochemical ozone formation": Wind,
  "Particulate matter": Factory,
  "Human toxicity, non-cancer": Skull,
  "Human toxicity, cancer": Skull,
  Acidification: Droplet,
  "Eutrophication, freshwater": Sprout,
  "Eutrophication, marine": Sprout,
  "Eutrophication, terrestrial": Leaf,
  "Ecotoxicity, freshwater": Droplet,
  "Land use": Mountain,
  "Water use": Droplet,
  "Resource use, fossils": Flame,
  "Resource use, minerals and metals": Triangle
};

// Different callers humanize indicator names differently (e.g. "Ecotoxicity, freshwater" vs
// "Ecotoxicity fresh water") -- normalize both sides before matching so the icon still resolves.
const normalize = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, "");

const categoryIconsByNormalizedName = new Map(
  Object.entries(categoryIcons).map(([name, icon]) => [normalize(name), icon])
);

// Group definitions
const groupLabels: Record<string, { label: string; keywords: string[] }> = {
  climate: { label: "Climate", keywords: ["climate", "ozone", "radiation"] },
  health: { label: "Health", keywords: ["toxicity", "particulate", "photochemical"] },
  ecosystem: { label: "Ecosystem", keywords: ["eutrophication", "ecotoxicity", "acidification"] },
  resources: { label: "Resources", keywords: ["resource", "land use", "water use"] }
};

/**
 * ImpactCardsGrid Component
 * Single Responsibility: Display impact data as interactive cards
 */
export const ImpactCardsGrid: React.FC<ImpactCardsGridProps> = ({ impactData, contextLabel }) => {
  const [activeGroup, setActiveGroup] = useState("all");

  // Get icon for category
  const getIcon = (indicator: string): LucideIcon => {
    return categoryIconsByNormalizedName.get(normalize(indicator)) ?? Recycle;
  };

  // Filter categories based on active group
  const filteredCategories = impactData.filter(cat => {
    if (activeGroup === "all") return true;
    const group = groupLabels[activeGroup];
    if (!group) return true;
    return group.keywords.some(keyword =>
      cat.indicator.toLowerCase().includes(keyword.toLowerCase())
    );
  });

  // Format value
  const formatValue = (value: number): string => {
    return String(formatNumberToScientific(value, 5));
  };

  if (impactData.length === 0) {
    return (
      <div className="tw-text-center tw-py-8 tw-text-muted-foreground">
        No impact data available
      </div>
    );
  }

  return (
    <div>
      <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-3 tw-mb-3 ">
        <div>
          <p className="tw-text-sm tw-font-semibold tw-text-foreground">
            Environmental Impact Categories
          </p>
          <p className="tw-text-xs tw-text-muted-foreground tw-mt-0.5">
            {contextLabel ? `${contextLabel} · ` : ""}
            {impactData.length} impact categories following EF 3.1 methodology
          </p>
        </div>
        <Tabs value={activeGroup} onValueChange={setActiveGroup}>
          <TabsList className="tw-h-11">
            <TabsTrigger value="all" className="tw-text-xs tw-px-3 tw-h-10">
              All
            </TabsTrigger>
            {Object.entries(groupLabels).map(([key, { label }]) => (
              <TabsTrigger key={key} value={key} className="tw-text-xs tw-px-2 tw-h-10">
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3">
        {filteredCategories.map((cat, index) => {
          const Icon = getIcon(cat.indicator);
          return (
            <Card
              key={index}
              className="tw-border-border hover:tw-border-primary/30 hover:tw-shadow-md tw-transition-all tw-duration-200 tw-group tw-cursor-default tw-p-0"
            >
              <CardContent className="tw-p-2">
                <div className="tw-flex tw-items-start tw-justify-between tw-mb-1">
                  <div className="tw-h-8 tw-w-8 tw-rounded-lg tw-bg-primary/10 tw-flex tw-items-center tw-justify-center group-hover:tw-bg-primary/15 tw-transition-colors">
                    <Icon className="tw-h-4 tw-w-4 tw-text-primary" />
                  </div>
                </div>
                <p className="tw-text-xs tw-font-medium tw-text-foreground tw-mb-0.5 tw-leading-tight">
                  {cat.indicator}
                </p>
                <p className="tw-text-lg tw-font-bold tw-text-foreground tw-leading-tight">
                  {formatValue(cat.measure)}
                </p>
                <p className="tw-text-[10px] tw-text-muted-foreground tw-mt-0.5">{cat.unit}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
